import moment from 'moment-timezone';
import { delay } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { addFilterPayload } from 'components/InfiniteTableSelfStandingV3/saga';
import config from 'config/config';
import { setGlobalError } from 'containers/ErrorHandler/actions';
import { getFile, setLoadingExportFlag } from 'containers/GlobalExport/actions';
import {
  EXPORT_ITEM,
  FILE_POLLING_DELAY,
  GET_EXPORT_FILE,
  MAX_FILE_POLLING_ATTEMPTS,
} from 'containers/GlobalExport/constants';
import { setPopoupBlockerObject } from 'containers/GlobalWrapper/actions';
import { selectRouteParams } from 'containers/GlobalWrapper/selectors';
import { clearCustomizationConfigs } from 'containers/OneProjectPage/ExportPage/store/actions';
import {
  selectActiveFilters,
  selectCustomizationConfigs,
  selectRecipientIds,
} from 'containers/OneProjectPage/ExportPage/store/selectors';
import globalMessages from 'translations/messages/global-messages';
import Analytics from 'utils/Analytics';
import checkOrSetSlash from 'utils/checkOrSetSlash';
import { oneProjectPages } from 'utils/constants';
import { applyTenantFilterTransform } from 'utils/dynamicProperties';
import { checkParams } from 'utils/errors/check-params';
import localStorageUser from 'utils/localStorageUser';
import request from 'utils/request';

export function* exportItem({ callback, exportFromForms = false }) {
  yield put(setLoadingExportFlag(true));

  const route = yield select(selectRouteParams());
  const filters = yield select(selectActiveFilters());
  const recipientIds = yield select(selectRecipientIds());
  const customizationConfigs = yield select(selectCustomizationConfigs());
  const persistentConfigs =
    localStorageUser.getReportCustomization({ projectId: route.idData }) || {};
  const configs = { ...customizationConfigs, ...persistentConfigs };
  const tempFilters = addFilterPayload({}, { ...filters, ...configs }, exportFromForms);

  if (filters.title) {
    Analytics.track('reports_title_added');
  }

  if (filters.description) {
    Analytics.track('reports_paragraph_added');
  }

  localStorageUser.saveFilterByProjectAndModule({
    filters,
    projectId: route.idData,
    module: oneProjectPages.export,
  });

  const generation_type = 'PDF';
  yield put(clearCustomizationConfigs());

  const requestURL = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/projects/${
    route.idData
  }/report/handover/generate`;

  const { module_ids, ...tempsFiltersWithoutModuleIds } = tempFilters;

  const tempsFiltersComplete =
    module_ids && module_ids[0] !== undefined
      ? { ...tempsFiltersWithoutModuleIds, module_ids: tempFilters.module_ids }
      : tempsFiltersWithoutModuleIds;

  let cleanedFilters = {};

  if (tempsFiltersComplete) {
    const { assignee_ids, creator_ids, tenant_dynamic_property_id, ...rest } = tempsFiltersComplete;

    const mapToUserIds = list =>
      Array.isArray(list)
        ? list.map(item => {
            if (item && typeof item === 'object' && 'user_id' in item) {
              return item.user_id;
            }
            return item;
          })
        : [];

    // Rewrite tenant_ids → dynamic_properties_values[tenantDynamicPropertyId] so the
    // export API receives the correct shape (see applyTenantFilterTransform for the
    // null / "" / UUID mapping details).
    const filtersWithTransformedTenants = applyTenantFilterTransform(
      rest,
      tenant_dynamic_property_id,
    );

    const { tenant_ids: _tenantIds, ...finalRestFilters } = filtersWithTransformedTenants;

    cleanedFilters = {
      ...finalRestFilters,
      assignee_ids: mapToUserIds(assignee_ids),
      creator_ids: mapToUserIds(creator_ids),
    };
  }

  const payload = {
    organization_id: route.organizationId,
    'api-version': '1.2',
    time_zone: moment.tz.guess(),
    generation_type,
    recipient_ids: recipientIds,
    ...cleanedFilters,
  };

  if (tempFilters.split_by) {
    payload.split_by = 'Company';
  } else {
    delete payload.split_by;
  }
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  };

  const data = yield call(request, requestURL, options, { errorCustom: true });
  if (data && callback) {
    yield call(callback, data, payload);
  }
  yield put(setLoadingExportFlag(false));
}

export function* fetchFileUrl({ params, key, entityType, pollingAttempt = 0 }) {
  if (key) {
    checkParams({ params, keys: ['idData'] });
    const requestURL = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/projects/${
      params.idData
    }/report/file?key=${key}`;

    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };
    const data = yield call(request, requestURL, options);

    if (!data?.url && (data?.is_in_error || data?.response?.is_in_error || data?.message)) {
      yield put(setLoadingExportFlag(false));
    } else if (!data?.url && pollingAttempt < MAX_FILE_POLLING_ATTEMPTS) {
      yield delay(FILE_POLLING_DELAY);
      yield put(getFile(params, key, entityType, pollingAttempt + 1));
    } else if (!data?.url) {
      yield put(setLoadingExportFlag(false));
      yield put(
        setGlobalError({
          data: {},
          customErrorMessageIntl: globalMessages.export_fail,
        }),
      );
    } else if (data && !data.message && data.url) {
      yield put(setLoadingExportFlag(false));
      yield put(setPopoupBlockerObject({ url: data.url }));
    }
  } else {
    yield put(
      setGlobalError({
        data: {},
        customErrorMessageIntl: globalMessages.export_fail,
      }),
    );
  }
}

export default function* initGlobalExportSaga() {
  yield takeLatest(EXPORT_ITEM, exportItem);
  yield takeLatest(GET_EXPORT_FILE, fetchFileUrl);
}
